import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Form } from "@/components/ui/form";
import { FormSelectSearchable } from "@/components/forms/form-select-searchable";
import { FormMultipleComboboxAsync } from "@/components/forms";
import { Separator } from "@/components/ui/separator";

import { useWorkSpaceStore } from "@/stores/store";
import {
  usePutData,
  useGetData,
  useInvalidateQueries,
} from "@/hooks/services/use-query-hooks";
import {
  UpdateTechnician,
  TechnicianById,
} from "@/hooks/services/pps/handlers/technicians-api";
import { ShopList } from "@/hooks/services/pps/handlers/shop-api";
import { WorkspaceList } from "@/hooks/services/pps/handlers/workspace-api";
import { machineMultipleApiConfig } from "./search-combobox-configs";
import { toastManager } from "@/lib/toast-manager";
import { Technician } from "@/hooks/services/pps/types/technicians.type";
import { SaveOutlined } from "@mui/icons-material";

const formSchema = z.object({
  shopId: z.coerce.number().min(1, "Please select a shop"),
  workspaceId: z.coerce.number().min(1, "Workspace ID is required"),
  machineIds: z.array(z.number()).optional().default([]),
});

type FormData = z.infer<typeof formSchema>;

interface EditTechnicianSheetProps {
  technician: Technician;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function EditTechnicianSheet({
  technician,
  onSuccess,
  trigger,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: EditTechnicianSheetProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number>(
    technician.workspace?.id || 0,
  );
  const [selectedShopId, setSelectedShopId] = useState<number>(
    technician.shop?.id || 0,
  );
  const { workspace } = useWorkSpaceStore();
  const invalidate = useInvalidateQueries();

  // Fetch workspaces data
  const { data: workspacesData } = useGetData<{ data: any[] }>(
    WorkspaceList({ page: 1, pageSize: 100 }),
  );

  // Fetch current technician details with machines when sheet is open
  const { data: technicianData, refetch: refetchTechnician } = useGetData<{
    data: Technician;
  }>(TechnicianById(String(technician.id)), {
    enabled: open, // Only fetch when sheet is open
  });

  const workspaceOptions = useMemo(
    () =>
      workspacesData?.data?.map((ws) => ({
        value: ws.id,
        label: ws.workspaceName,
        raw: ws,
      })) || [],
    [workspacesData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      shopId: technician.shop?.id || 0,
      workspaceId: technician.workspace?.id || workspace.id || 0,
      machineIds: technician.machines?.map((m) => m.id) || [],
    },
  });

  useEffect(() => {
    if (open) {
      // Use fetched technician data if available, otherwise fallback to prop
      const currentTechnician = technicianData?.data || technician;

      form.reset({
        shopId: currentTechnician.shop?.id || 0,
        workspaceId: currentTechnician.workspace?.id || workspace.id || 0,
        machineIds: currentTechnician.machines?.map((m) => m.id) || [],
      });
      setSelectedWorkspaceId(
        currentTechnician.workspace?.id || workspace.id || 0,
      );
      setSelectedShopId(currentTechnician.shop?.id || 0);

      // Refetch technician data to get latest machines
      refetchTechnician();
    }
  }, [open, technician, technicianData, workspace.id, form, refetchTechnician]);

  const workspaceId = form.watch("workspaceId");
  const shopId = form.watch("shopId");

  // Fetch shops data filtered by selected workspace
  const { data: shopsData } = useGetData<{ data: any[] }>(
    ShopList({ search: "", workspaceId: workspaceId || 0 }),
    {
      enabled: !!workspaceId && workspaceId > 0,
    },
  );

  const shopOptions = useMemo(
    () =>
      shopsData?.data?.map((shop) => ({
        value: shop.id,
        label: shop.shopName,
        raw: shop,
      })) || [],
    [shopsData],
  );

  // Reset shop and machines when workspace changes
  useEffect(() => {
    if (workspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(workspaceId);
      setSelectedShopId(0);
      form.setValue("shopId", 0);
      form.setValue("machineIds", []);
    }
  }, [workspaceId, selectedWorkspaceId, form]);

  // Reset machines when shop changes
  useEffect(() => {
    if (shopId !== selectedShopId) {
      setSelectedShopId(shopId);
      form.setValue("machineIds", []);
    }
  }, [shopId, selectedShopId, form]);

  const updateTechnicianMutation = usePutData(
    UpdateTechnician(String(technician.id)),
  );

  const onSubmit = async (data: FormData) => {
    updateTechnicianMutation.mutate(data, {
      onSuccess: () => {
        toastManager.showSuccess("Technician updated successfully");

        form.reset();
        setOpen(false);
        onSuccess?.();
      },
      onError: (error: any) => {
        toastManager.showError(
          "Failed to update technician",
          error?.message || "Something went wrong",
        );
      },
      onSettled: () => {
        invalidate([
          ["TechnicianList"],
          ["TechnicianGroupedByShop"],
          ["MachineListGroupedByShop"],
          ["TechnicianGroupedByShop"],
          ["JobSchedule"],
          ["TechnicianSchedule"],
          ["MachineSchedule"],
        ]);
      },
    });
  };

  const handleCancel = () => {
    form.reset();
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="aero:w-full aero:sm:max-w-[500px] aero:p-0 aero:flex aero:flex-col aero:gap-0">
        <SheetHeader className="aero:p-6 aero:pb-4">
          <div className="aero:flex aero:items-center aero:justify-between">
            <SheetTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-neutral-900">
              Edit Technician
            </SheetTitle>
          </div>
          <Separator className="aero:mt-4" />
        </SheetHeader>

        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6">
          <Form {...form}>
            <form className="aero:space-y-4">
              <div className="aero:space-y-2">
                <label className="aero:text-sm aero:font-medium aero:text-neutral-700">
                  Name
                </label>
                <input
                  disabled
                  value={technician.name}
                  className="aero:h-10 aero:w-full aero:rounded-md aero:border aero:border-neutral-300 aero:bg-neutral-100 aero:px-3 aero:py-2 aero:text-sm aero:text-neutral-500 aero:cursor-not-allowed"
                />
              </div>

              <div className="aero:space-y-2">
                <label className="aero:text-sm aero:font-medium aero:text-neutral-700">
                  Employee Number
                </label>
                <input
                  disabled
                  value={technician.employeeNumber || "N/A"}
                  className="aero:h-10 aero:w-full aero:rounded-md aero:border aero:border-neutral-300 aero:bg-neutral-100 aero:px-3 aero:py-2 aero:text-sm aero:text-neutral-500 aero:cursor-not-allowed"
                />
              </div>

              <FormSelectSearchable
                control={form.control}
                name="workspaceId"
                label="Workspace"
                options={workspaceOptions}
                placeholder="Select a workspace"
                triggerClassName="aero:h-10"
                required
                labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                wrapperClassName="aero:space-y-2"
              />

              <FormSelectSearchable
                control={form.control}
                name="shopId"
                label="Shop"
                options={shopOptions}
                placeholder="Select a shop"
                triggerClassName="aero:h-10"
                required
                labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                wrapperClassName="aero:space-y-2"
              />

              <div className="aero:space-y-3">
                <FormMultipleComboboxAsync
                  control={form.control}
                  name="machineIds"
                  label="Machines"
                  placeholder="Search and select machines..."
                  wrapperClassName="aero:space-y-2"
                  labelClassName="aero:text-sm aero:font-medium aero:text-neutral-700"
                  apiConfig={machineMultipleApiConfig(
                    selectedWorkspaceId || 0,
                    selectedShopId,
                  )}
                  initialItems={
                    technicianData?.data?.machines?.map((machine) => ({
                      id: machine.id,
                      label: `${machine.machineId} - ${machine.name}`,
                      value: machine.id,
                      subtitle: machine.shop?.shopName || "",
                      raw: machine,
                    })) || []
                  }
                  disabled={!selectedShopId}
                />
              </div>

              {selectedShopId === 0 && (
                <div className="aero:text-xs aero:text-amber-600 aero:bg-amber-50 aero:p-3 aero:rounded-md aero:border aero:border-amber-200">
                  Please select a shop first to enable machine selection
                </div>
              )}
            </form>
          </Form>
        </div>

        <div className="aero:flex aero:gap-4 aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-200 aero:space-y-3">
          <Button
            onClick={handleCancel}
            disabled={updateTechnicianMutation.isPending}
            intent="neutral"
            design="outline"
            className="aero:w-full aero:h-10"
          >
            <X className="aero:w-4 aero:h-4" />
            Cancel
          </Button>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={updateTechnicianMutation.isPending}
            className="aero:w-full aero:h-10"
            intent="primary"
          >
            {" "}
            <SaveOutlined className="aero:w-4 aero:h-4" />
            {updateTechnicianMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
